import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import VideoPlayer from './components/VideoPlayer';
import FilterSystem from './components/FilterSystem';
import VideoThumbnailGrid from './components/VideoThumbnailGrid';
import ViewingQueue from './components/ViewingQueue';
import { propertyService } from '../../services/propertyService';
import EmptyState from '../../components/ui/EmptyState';

const VideoTourDiscoveryCenter = () => {
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    commune: '',
    propertyType: '',
    priceRange: '',
    tourDuration: ''
  });
  const [viewingQueue, setViewingQueue] = useState([]);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  useEffect(() => {
    loadPropertiesWithVideos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [properties, filters]);

  const loadPropertiesWithVideos = async () => {
    try {
      setLoading(true);
      const { data, error } = await propertyService?.searchProperties({
        status: 'available'
      });
      
      if (error) {
        setError(error);
        return;
      }
      
      // Filter only properties with videos
      const propertiesWithVideos = Array.isArray(data) ? data?.filter(property => 
        property?.videos && 
        Array.isArray(property?.videos) && 
        property?.videos?.length > 0
      ) : [];

      setProperties(propertiesWithVideos);
      
      // Set first video as current if available
      if (propertiesWithVideos?.length > 0 && propertiesWithVideos?.[0]?.videos?.[0]) {
        setCurrentVideo({
          property: propertiesWithVideos?.[0],
          video: propertiesWithVideos?.[0]?.videos?.[0],
          index: 0
        });
      }
    } catch (err) {
      setError('Erreur lors du chargement des visites vidéo');
      console.error('Error loading properties with videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...properties];

    if (filters?.commune) {
      filtered = filtered?.filter(property => 
        property?.communes?.name?.toLowerCase()?.includes(filters?.commune?.toLowerCase())
      );
    }

    if (filters?.propertyType) {
      filtered = filtered?.filter(property => 
        property?.property_type === filters?.propertyType
      );
    }

    if (filters?.priceRange) {
      const [min, max] = filters?.priceRange?.split('-')?.map(Number);
      filtered = filtered?.filter(property => {
        const price = property?.price;
        if (max) {
          return price >= min && price <= max;
        }
        return price >= min;
      });
    }

    if (filters?.tourDuration) {
      filtered = filtered?.filter(property =>
        property?.videos?.some(video => {
          const duration = video?.duration || 300; // Default 5 minutes
          switch (filters?.tourDuration) {
            case 'short': return duration <= 300; // ≤ 5 min
            case 'medium': return duration > 300 && duration <= 600; // 5-10 min
            case 'long': return duration > 600; // > 10 min
            default: return true;
          }
        })
      );
    }

    setFilteredProperties(filtered);
  };

  const handleVideoSelect = (property, video, index) => {
    setCurrentVideo({ property, video, index });
  };

  const addToQueue = (property, video) => {
    const queueItem = {
      id: `${property?.id}_${video?.id}`,
      property,
      video,
      title: `${property?.title} - ${video?.title || 'Visite virtuelle'}`
    };

    setViewingQueue(prev => {
      const exists = prev?.some(item => item?.id === queueItem?.id);
      if (!exists) {
        return [...prev, queueItem];
      }
      return prev;
    });
  };

  const removeFromQueue = (queueId) => {
    setViewingQueue(prev => prev?.filter(item => item?.id !== queueId));
  };

  const playNextInQueue = () => {
    if (viewingQueue?.length > 0) {
      const nextItem = viewingQueue?.[0];
      setCurrentVideo({
        property: nextItem?.property,
        video: nextItem?.video,
        index: 0
      });
      removeFromQueue(nextItem?.id);
    }
  };

  const handleVideoEnd = () => {
    if (isAutoPlay) {
      playNextInQueue();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des visites vidéo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-[60vh] px-4">
          <EmptyState
            iconName="VideoOff"
            title="Erreur de chargement"
            description={typeof error === 'string' ? error : 'Impossible de charger les visites vidéo.'}
            ctaLabel="Réessayer"
            ctaIcon="RefreshCw"
            onCta={loadPropertiesWithVideos}
            size="lg"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Centre de Découverte des Visites Vidéo - Kapital States</title>
        <meta 
          name="description" 
          content="Explorez notre galerie de visites vidéo haute qualité. Découvrez des propriétés par commune et type avec des tours virtuels immersifs." 
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16 sm:pt-20">
          {/* Hero Section */}
          <section className="bg-card border-b border-border py-8 sm:py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-2xl sm:text-3xl sm:text-4xl font-heading font-display-semibold text-foreground mb-3 sm:mb-4">
                  Centre de Découverte des Visites Vidéo
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground max-w-3xl mx-auto">
                  Explorez nos propriétés avec des visites vidéo immersives et découvrez votre futur chez-vous
                </p>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {/* Mobile: filters toggle above content */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
              {/* Filters Sidebar — full width on mobile, sidebar on lg+ */}
              <div className="lg:col-span-1 space-y-4">
                <FilterSystem 
                  filters={filters}
                  onFiltersChange={setFilters}
                />
                
                <ViewingQueue 
                  queue={viewingQueue}
                  onRemoveFromQueue={removeFromQueue}
                  onPlayNext={playNextInQueue}
                  isAutoPlay={isAutoPlay}
                  onAutoPlayChange={setIsAutoPlay}
                />
              </div>

              {/* Video Content */}
              <div className="lg:col-span-3 space-y-6 sm:space-y-8">
                {/* Main Video Player */}
                {currentVideo && (
                  <VideoPlayer
                    video={currentVideo}
                    onVideoEnd={handleVideoEnd}
                    onAddToQueue={addToQueue}
                  />
                )}

                {/* Video Thumbnails Grid */}
                <VideoThumbnailGrid
                  properties={filteredProperties}
                  onVideoSelect={handleVideoSelect}
                  onAddToQueue={addToQueue}
                  currentVideo={currentVideo}
                />

                {/* No Results */}
                {filteredProperties?.length === 0 && (
                  <div className="text-center py-10 sm:py-12 px-4">
                    <p className="text-muted-foreground text-base">
                      Aucune visite vidéo ne correspond aux critères sélectionnés.
                    </p>
                    <button
                      onClick={() => setFilters({
                        commune: '',
                        propertyType: '',
                        priceRange: '',
                        tourDuration: ''
                      })}
                      className="mt-4 text-brand-gold hover:text-brand-gold/80 font-medium text-sm min-h-[44px] px-4 touch-manipulation"
                    >
                      Réinitialiser les filtres
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default VideoTourDiscoveryCenter;